import {createRouter, createWebHistory, type RouteRecordRaw} from 'vue-router';
import {useAuthStore} from '@/store/auth';
import type {UserRole} from '@/types/contracts';

declare module 'vue-router' {
    interface RouteMeta {
        public?: boolean;
        title: string;
        roles?: UserRole[];
        creatorTab?: string;
        /** 标记已迁移到营销盒管理台的页面。 */
        management?: boolean;
    }
}

const managementRoutes: RouteRecordRaw[] = [
    {
        path: '/dashboard',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue'),
        meta: {title: '首页', management: true},
    },
    {
        path: '/content-operations',
        name: 'content-operations',
        component: () => import('@/views/ContentOperationsView.vue'),
        meta: {title: '内容运营中心', management: true},
    },
    {
        path: '/content-operations/create',
        name: 'content-operations-create',
        component: () => import('@/views/ContentTaskCreateView.vue'),
        meta: {title: '创建内容任务', management: true},
    },
    {
        path: '/account',
        name: 'account',
        component: () => import('@/views/AccountView.vue'),
        meta: {title: '账户中心', management: true},
    },
    {
        path: '/account/knowledge/:documentId',
        name: 'account-knowledge-document',
        component: () => import('@/views/KnowledgeDocumentView.vue'),
        meta: {title: '知识文档', management: true},
    },
    {
        path: '/devices',
        name: 'devices',
        component: () => import('@/views/DevicesView.vue'),
        meta: {title: '营销盒', management: true},
    },
    {
        path: '/tasks',
        name: 'tasks',
        component: () => import('@/views/TasksView.vue'),
        meta: {title: '任务', management: true},
    },
    {
        path: '/platform-accounts',
        name: 'platform-accounts',
        component: () => import('@/views/PlatformAccountsView.vue'),
        meta: {title: '平台账号', management: true},
    },
    {
        path: '/content-operations/accounts',
        name: 'content-operations-accounts',
        component: () => import('@/views/PlatformAccountsView.vue'),
        meta: {title: '平台账号管理', management: true},
    },
    {
        path: '/content-operations/automation',
        name: 'content-operations-automation',
        component: () => import('@/views/AutomationView.vue'),
        meta: {title: '自动化监控', management: true},
    },
    {
        path: '/platform-accounts-login',
        name: 'platform-accounts-login',
        redirect: '/platform-accounts',
        meta: {title: '平台账号登录', management: true},
    },
    {
        path: '/admin/users',
        name: 'admin-users',
        component: () => import('@/views/AdminUsersView.vue'),
        meta: {title: '用户管理', roles: ['SUPER_ADMIN'], management: true},
    },
    {
        path: '/admin/devices',
        name: 'admin-devices',
        component: () => import('@/views/AdminDevicesView.vue'),
        meta: {title: '全部设备', roles: ['SUPER_ADMIN'], management: true},
    },
    {
        path: '/admin/marketing-box',
        name: 'admin-marketing-box',
        component: () => import('@/views/AdminMarketingBoxView.vue'),
        meta: {title: '营销盒资格', roles: ['SUPER_ADMIN'], management: true},
    },
    {
        path: '/admin/h5-config',
        name: 'admin-h5-config',
        component: () => import('@/views/AdminH5ConfigView.vue'),
        meta: {title: '手机 H5 配置', roles: ['SUPER_ADMIN'], management: true},
    },
];

// 尚未迁移的业务路由共享同一个失败关闭页面，禁止重新接入旧本地 HTTP API。
const creatorHubComponent = () => import('@/views/CreatorHubView.vue');

/**
 * 尚未完成迁移的 CreatorHub 业务路由。
 *
 * <p>保留原 URL 和业务名称，迁移完成前只展示边界说明，不加载冻结静态页面。</p>
 */
export const creatorHubRoutes: RouteRecordRaw[] = [
    {
        path: '/creatorhub',
        name: 'creatorhub',
        component: creatorHubComponent,
        meta: {title: 'CreatorHub 工作台', creatorTab: 'overview', management: false},
    },
    ...[
        ['content', 'hub', '内容'],
        ['collection', 'collections', '内容采集'],
        ['collection-keywords', 'collections', '关键词采集'],
        ['collection-share-links', 'collections', '分享链接'],
        ['monitoring-content', 'monitors', '内容监控'],
        ['monitoring-comments', 'comments', '评论监控'],
        ['monitoring-danmaku', 'danmaku', '弹幕监控'],
        ['publishing', 'publish', '内容发布'],
        ['engagement-comments', 'autocomment', '评论任务'],
        ['messages', 'accounts', '私信'],
        ['reports', 'overview', '报表'],
        ['settings-system', 'settings', '系统设置'],
        ['risk-control', 'risk-control', '风控'],
        ['notifications', 'notifications', '通知'],
        ['tasks-review-queue', 'queue', '任务队列'],
    ].map(([path, creatorTab, title]) => ({
        path: `/${path}`,
        name: `creatorhub-${path}`,
        component: creatorHubComponent,
        meta: {title, creatorTab, management: false},
    } as RouteRecordRaw)),
];

export const routes: RouteRecordRaw[] = [
    {
        path: '/login',
        name: 'login',
        component: () => import('@/views/LoginView.vue'),
        meta: {public: true, title: '登录'},
    },
    {
        path: '/',
        component: () => import('@/layouts/AppShell.vue'),
        meta: {title: '析宝智能营销盒'},
        children: [
            {path: '', redirect: '/dashboard'},
            ...managementRoutes.map((route) => ({
                ...route,
                path: route.path.replace(/^\//, ''),
            })),
        ],
    },
    ...creatorHubRoutes,
    {
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/views/NotFoundView.vue'),
        meta: {public: true, title: '页面不存在'}
    },
];

export function canAccessRoute(role: UserRole | undefined, roles?: UserRole[]): boolean {
    return !roles?.length || Boolean(role && roles.includes(role));
}

const router = createRouter({history: createWebHistory(), routes});

router.beforeEach(async (to) => {
    document.title = `${to.meta.title} | 析宝智能营销盒`;
    const auth = useAuthStore();
    await auth.initialize();
    if (to.meta.public) {
        if (to.name === 'login' && auth.isAuthenticated) return {name: 'dashboard'};
        return true;
    }
    if (!auth.isAuthenticated) return {name: 'login', query: {redirect: to.fullPath}};
    if (!canAccessRoute(auth.user?.role, to.meta.roles)) return {name: 'dashboard'};
    return true;
});

export default router;
